import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { listProjects } from '@/services/projectService';
import { getMlMetrics, type MlMetricsResponse } from '@/services/adminService';
import type { ScriptProject } from '@/types/scriptProject';

function pct(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(rate)) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

function ms(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (value < 1000) return `${Math.round(value)} ms`;
  return `${(value / 1000).toFixed(2)} s`;
}

export default function MlMetricsPanel() {
  const [projects, setProjects] = useState<ScriptProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MlMetricsResponse | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      const list = await listProjects();
      setProjects(list);
      setSelectedProjectId((prev) => {
        if (prev && list.some((p) => p.id === prev)) return prev;
        return list[0]?.id ?? null;
      });
    } catch {
      setProjects([]);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metrics = await getMlMetrics({
        projectId: selectedProjectId ?? undefined,
        limit: 40,
        days,
      });
      setData(metrics);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ML metrics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, days]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  const summaryCards = useMemo(() => {
    const s = data?.summary;
    return [
      { label: 'ML success', value: pct(s?.success_rate), sub: `${s?.ml_ok ?? 0} / ${s?.attempts ?? 0}` },
      { label: 'Fall-through', value: pct(s?.fallthrough_rate), sub: `${s?.fallthrough ?? 0} steps` },
      { label: 'Latency p95', value: ms(s?.latency_p95_ms), sub: `${s?.latency_samples ?? 0} samples` },
      {
        label: 'Scripts scanned',
        value: String(data?.scripts_scanned ?? 0),
        sub: `${data?.ml_steps_scanned ?? 0} ML steps`,
      },
    ];
  }, [data]);

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-mono text-white/45 max-w-2xl">
          Per-model ML success rate, fall-through rate, and latency p95 from recent methodology runs.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-mono text-white/40">
            Mandate
            <select
              className="ml-2 rounded border border-[#2a2a2a] bg-[#141414] px-2 py-1.5 text-sm text-white"
              value={selectedProjectId ?? ''}
              onChange={(e) => setSelectedProjectId(e.target.value || null)}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-mono text-white/40">
            Days
            <select
              className="ml-2 rounded border border-[#2a2a2a] bg-[#141414] px-2 py-1.5 text-sm text-white"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {[7, 14, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <Button variant="indexOutline" size="sm" onClick={() => void loadMetrics()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-white/50 font-sans text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading ML metrics…
        </div>
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {summaryCards.map((card) => (
              <div
                key={card.label}
                className="rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-4 py-3"
              >
                <p className="text-[10px] font-mono uppercase tracking-wider text-white/40">
                  {card.label}
                </p>
                <p className="mt-1 text-2xl font-sans text-white">{card.value}</p>
                <p className="mt-1 text-xs font-mono text-white/40">{card.sub}</p>
              </div>
            ))}
          </div>

          {data?.summary?.latency_note && (
            <p className="mb-4 text-[11px] font-mono text-white/35">{data.summary.latency_note}</p>
          )}

          <div className="overflow-x-auto rounded-lg border border-[#2a2a2a]">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-[#141414] text-[10px] font-mono uppercase tracking-wider text-white/45">
                <tr>
                  <th className="px-3 py-2">Executor</th>
                  <th className="px-3 py-2">Attempts</th>
                  <th className="px-3 py-2">Success</th>
                  <th className="px-3 py-2">Fall-through</th>
                  <th className="px-3 py-2">Failed</th>
                  <th className="px-3 py-2">p95 latency</th>
                </tr>
              </thead>
              <tbody>
                {(data?.executors ?? []).map((row) => (
                  <tr key={row.executor_id} className="border-t border-[#2a2a2a]">
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs text-white">{row.executor_id}</div>
                      <div className="text-[11px] text-white/40">{row.slug}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-white/70">{row.attempts}</td>
                    <td
                      className={cn(
                        'px-3 py-2 font-mono',
                        (row.success_rate ?? 0) >= 0.9 ? 'text-green-300' : 'text-white/70',
                      )}
                    >
                      {pct(row.success_rate)}
                      <span className="text-white/35"> ({row.ml_ok})</span>
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 font-mono',
                        (row.fallthrough_rate ?? 0) > 0 ? 'text-amber-200' : 'text-white/70',
                      )}
                    >
                      {pct(row.fallthrough_rate)}
                      <span className="text-white/35"> ({row.fallthrough})</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-white/70">{row.failed}</td>
                    <td className="px-3 py-2 font-mono text-white/70">
                      {ms(row.latency_p95_ms)}
                      {row.latency_samples === 0 && (
                        <span className="text-white/30"> (n/a)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
